/**
 * "Confirm your email address" — roadmap 4.3, §8.1.
 *
 * An unverified account can browse but cannot enrol, buy or upload: `IsVerified`
 * rejects those endpoints. Without this banner the first the user hears of it is
 * a 403 on the action they came to perform, which reads as a broken product
 * rather than a step they have not finished.
 *
 * A banner rather than a modal, and dismissable by ignoring it — the account
 * still works for everything else, so blocking the page would be a lie about the
 * severity.
 */
import { MailWarning } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { isApiError } from '@/shared/api/problem'
import { useResendVerification } from '@/shared/auth/hooks'
import { useSession } from '@/shared/auth/useSession'
import { Button } from '@/shared/ui'

export function VerificationBanner() {
  const { t } = useTranslation('auth')
  const session = useSession()
  const resend = useResendVerification()

  if (session.status !== 'authenticated' || session.isVerified) return null

  const identifier = session.user?.email ?? session.user?.phone ?? ''
  if (!identifier) return null

  // Same discrimination `LoginScreen` uses for its own 429: the throttled
  // case gets specific, actionable copy; anything else gets a plain "it
  // didn't send, try again" rather than a raw problem detail.
  const resendError =
    resend.isError && isApiError(resend.error) && resend.error.code === 'throttled'
      ? t('account.verifyRateLimited')
      : resend.isError
        ? t('account.verifyFailed')
        : null

  return (
    <div className="border-b border-warning/40 bg-warning-soft">
      <div className="flex flex-wrap items-center gap-3 px-gutter py-2 text-sm">
        <MailWarning aria-hidden="true" className="size-4 shrink-0 text-warning" />
        <p className="min-w-0">{t('account.verifyBanner')}</p>
        <Button
          variant="secondary"
          size="sm"
          className="ms-auto"
          loading={resend.isPending}
          disabled={resend.isSuccess}
          onClick={() => resend.mutate({ identifier })}
        >
          {resend.isSuccess ? t('account.verifySent') : t('account.verifyAction')}
        </Button>
        {resendError ? (
          <p role="alert" className="w-full text-xs text-danger">
            {resendError}
          </p>
        ) : null}
      </div>
    </div>
  )
}
